import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIncident, updateIncident, triggerEscalation, useIncidentContext, useIncidentTimeEntries, createTimeEntry, useIncidentTimeline, playbookFromIncident, createPlaybook, investigateIncident } from '../../hooks/useSecOps';
import type { IncidentContext, TimeEntry, IncidentTimelineEntry, ArgusInvestigation } from '../../hooks/useSecOps';
import { useSignalRRefresh } from '../../hooks/useSignalR';
import { Bell, Clock, Building2, Monitor, History, ShieldAlert, Plus, Sparkles } from 'lucide-react';
import { SourceTicketCard } from '../../components/secops/SourceTicketCard';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  contained: 'bg-orange-100 text-orange-700',
  eradicated: 'bg-teal-100 text-teal-700',
  recovered: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  post_incident: 'bg-purple-100 text-purple-700',
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  new: ['open', 'investigating'],
  open: ['investigating', 'contained'],
  investigating: ['contained', 'eradicated'],
  contained: ['eradicated'],
  eradicated: ['recovered'],
  recovered: ['closed', 'post_incident'],
  post_incident: ['closed'],
  closed: ['open'],
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useIncident(id ?? null);
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeDesc, setTimeDesc] = useState('');
  const [timeBillable, setTimeBillable] = useState(true);
  const [addingTime, setAddingTime] = useState(false);
  const [generatingPlaybook, setGeneratingPlaybook] = useState(false);
  const [showPlaybookConfirm, setShowPlaybookConfirm] = useState(false);
  const [generatedPlaybook, setGeneratedPlaybook] = useState<Record<string, unknown> | null>(null);
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  const [investigating, setInvestigating] = useState(false);
  const [argusInvestigation, setArgusInvestigation] = useState<ArgusInvestigation | null>(null);
  const [argusError, setArgusError] = useState<string | null>(null);

  const { data: contextData } = useIncidentContext(id ?? null);
  const { data: timelineData, refresh: refreshTimeline } = useIncidentTimeline(id ?? null);
  const { data: timeData, refresh: refreshTime } = useIncidentTimeEntries(id ?? null);
  useSignalRRefresh('incidentUpdate', () => { refresh(); refreshTimeline(); });
  const enrichedContext = (contextData as { data?: IncidentContext })?.data;
  const timeEntries = (timeData as { data?: TimeEntry[]; total_minutes?: number })?.data ?? [];
  const totalMinutes = (timeData as { total_minutes?: number })?.total_minutes ?? 0;

  const incident = data as Record<string, unknown> | null;

  async function changeStatus(newStatus: string) {
    if (!id) return;
    setUpdating(true);
    try {
      await updateIncident(id, { status: newStatus });
      refresh();
    } catch { /* handled */ } finally {
      setUpdating(false);
    }
  }

  async function handleEscalate() {
    if (!id) return;
    setEscalating(true);
    try {
      await triggerEscalation({ incident_id: id });
      setEscalated(true);
      refresh();
    } catch { /* handled */ } finally {
      setEscalating(false);
    }
  }

  async function addTimelineNote() {
    if (!id || !note.trim()) return;
    setAddingNote(true);
    try {
      await updateIncident(id, { add_timeline_entry: { type: 'note', text: note.trim() } });
      setNote('');
      refresh();
    } catch { /* handled */ } finally {
      setAddingNote(false);
    }
  }

  async function handleLogTime() {
    if (!id || !timeMinutes) return;
    const mins = parseInt(timeMinutes, 10);
    if (isNaN(mins) || mins <= 0) return;
    setAddingTime(true);
    try {
      await createTimeEntry(id, { duration_minutes: mins, description: timeDesc || undefined, billable: timeBillable });
      setTimeMinutes('');
      setTimeDesc('');
      refreshTime();
      refresh();
    } catch { /* handled */ } finally {
      setAddingTime(false);
    }
  }

  async function handleGeneratePlaybook() {
    if (!id) return;
    setGeneratingPlaybook(true);
    try {
      const result = await playbookFromIncident(id);
      setGeneratedPlaybook(result.data);
      setShowPlaybookConfirm(true);
    } catch { /* handled */ } finally {
      setGeneratingPlaybook(false);
    }
  }

  async function handleInvestigate() {
    if (!id) return;
    setInvestigating(true);
    setArgusError(null);
    try {
      const result = await investigateIncident(id);
      setArgusInvestigation(result.investigation);
    } catch (err) {
      setArgusError(err instanceof Error ? err.message : 'ARGUS investigation failed');
    } finally {
      setInvestigating(false);
    }
  }

  async function handleConfirmPlaybook() {
    if (!generatedPlaybook) return;
    setSavingPlaybook(true);
    try {
      await createPlaybook(generatedPlaybook);
      setShowPlaybookConfirm(false);
      setGeneratedPlaybook(null);
      navigate('/secops/playbooks');
    } catch { /* handled */ } finally {
      setSavingPlaybook(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load incident</p>
          <p className="text-red-500 text-sm mt-1">{error ?? 'Incident not found'}</p>
          <button onClick={refresh} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  // The API wraps the single-incident response in { data: ... }
  const incidentDoc = (incident?.data as Record<string, unknown>) ?? incident;
  const status = (incidentDoc.status as string) ?? (incident.status as string) ?? 'new';
  const transitions = STATUS_TRANSITIONS[status] ?? [];
  const timeline = (incident.timeline as Array<{ type: string; text: string; timestamp: string; author?: string }>) ?? [];
  const linkedAlerts = (incident.linked_alerts as Array<{ id: string; title: string; severity: string }>) ?? [];
  const linkedPlaybooks = (incident.linked_playbooks as Array<{ id: string; name: string; status: string }>) ?? [];
  const responseActions = (incident.response_actions as Array<{ id: string; action_type: string; target: string; status: string }>) ?? [];

  // PSA source ticket (present when escalated via "Escalate to Security" in PSA)
  const sourcePsaTicketId = (incidentDoc.source_psa_ticket_id as string | undefined) ?? (incident.source_psa_ticket_id as string | undefined);
  const sourcePsaTicketNumber = (incidentDoc.source_psa_ticket_number as string | undefined) ?? (incident.source_psa_ticket_number as string | undefined);
  const sourcePsaTicketUrl = (incidentDoc.source_psa_ticket_url as string | undefined) ?? (incident.source_psa_ticket_url as string | undefined);

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <button onClick={() => navigate('/secops/incidents')} className="text-sm text-gray-500 hover:text-gray-700">
        &larr; Back to Incidents
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{incident.title as string}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge value={incident.severity as string} colors={SEVERITY_COLORS} />
            <Badge value={status} colors={STATUS_COLORS} />
            <span className="text-sm text-gray-500">Assigned: {(incident.assigned_to as string) || 'Unassigned'}</span>
            <span className="text-sm text-gray-500">Opened: {incident.opened_at ? new Date(incident.opened_at as string).toLocaleString() : '-'}</span>
          </div>
        </div>
      </div>

      {/* Status transition buttons + Escalation */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Transition to:</span>
        {transitions.length === 0 ? (
          <span className="text-sm text-gray-400">No available transitions</span>
        ) : (
          transitions.map(next => (
            <button key={next} onClick={() => changeStatus(next)} disabled={updating}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-40 capitalize">
              {next.replace(/_/g, ' ')}
            </button>
          ))
        )}
        <div className="ml-auto flex items-center gap-2">
          {escalated && <span className="text-xs text-green-600 font-medium">Escalation triggered</span>}
          {(status === 'closed' || status === 'recovered') && (
            <button
              onClick={handleGeneratePlaybook}
              disabled={generatingPlaybook}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-40"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {generatingPlaybook ? 'Generating...' : 'Generate Playbook from Incident'}
            </button>
          )}
          <button
            onClick={handleInvestigate}
            disabled={investigating}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-40"
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            {investigating ? 'Investigating…' : 'Investigate with ARGUS'}
          </button>
          {status !== 'closed' && status !== 'resolved' && (
            <button
              onClick={handleEscalate}
              disabled={escalating}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
            >
              <Bell className="w-3.5 h-3.5" />
              {escalating ? 'Escalating…' : 'Escalate'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Timeline</h2>
            {timeline.length === 0 && <p className="text-sm text-gray-400 mb-4">No timeline entries yet.</p>}
            <div className="space-y-4 mb-4">
              {timeline.map((entry, i) => (
                <div key={i} className="flex gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    entry.type === 'status_change' ? 'bg-indigo-500' :
                    entry.type === 'note' ? 'bg-gray-400' :
                    entry.type === 'action' ? 'bg-orange-500' : 'bg-gray-300'
                  }`} />
                  <div>
                    <p className="text-sm text-gray-700">{entry.text}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {entry.author && <span className="font-medium">{entry.author} · </span>}
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 border-t border-gray-100 pt-4">
              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Add a timeline note..."
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                onKeyDown={e => e.key === 'Enter' && addTimelineNote()} />
              <button onClick={addTimelineNote} disabled={addingNote || !note.trim()}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {addingNote ? '...' : 'Add Note'}
              </button>
            </div>
          </div>

          {/* AI Narrative */}
          {(() => {
            const apiTimeline = (timelineData as { data?: IncidentTimelineEntry[] })?.data ?? [];
            const aiEntry = apiTimeline.find(e => e.event_type === 'ai_narrative');
            if (!aiEntry) return null;
            const meta = aiEntry.metadata as {
              mitre_mapping?: Array<{ technique_id: string; technique_name: string; relevance: string }>;
              attack_phase_summary?: string;
              recommended_actions?: string[];
              risk_assessment?: string;
            } | undefined;
            return (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
                <h2 className="text-base font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" /> AI Attack Narrative
                </h2>
                <p className="text-sm text-gray-700 whitespace-pre-wrap mb-4">{aiEntry.description}</p>
                {meta?.attack_phase_summary && (
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-1">Attack Phase</h3>
                    <p className="text-sm text-gray-700">{meta.attack_phase_summary}</p>
                  </div>
                )}
                {meta?.risk_assessment && (
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-1">Risk Assessment</h3>
                    <p className="text-sm text-gray-700">{meta.risk_assessment}</p>
                  </div>
                )}
                {meta?.recommended_actions && meta.recommended_actions.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-1">Recommended Actions</h3>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {meta.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}
                {meta?.mitre_mapping && meta.mitre_mapping.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-1">MITRE ATT&CK</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {meta.mitre_mapping.map((m, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-white border border-indigo-200 rounded px-2 py-0.5 text-xs" title={m.relevance}>
                          <span className="font-mono font-medium text-indigo-700">{m.technique_id}</span>
                          <span className="text-gray-500">{m.technique_name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-indigo-400 mt-3">Generated by AI · {new Date(aiEntry.created_at).toLocaleString()}</p>
              </div>
            );
          })()}

          {/* Compliance Impact */}
          {(() => {
            const apiTimeline2 = (timelineData as { data?: IncidentTimelineEntry[] })?.data ?? [];
            const compEntry = apiTimeline2.find(e => e.event_type === 'compliance_mapping');
            if (!compEntry) return null;
            const meta = compEntry.metadata as {
              controls?: Array<{ framework: string; control_id: string; control_name: string }>;
              frameworks_affected?: string[];
            } | undefined;
            const FRAMEWORK_LABELS: Record<string, string> = {
              nist_800_171: 'NIST 800-171', soc2: 'SOC 2', hipaa: 'HIPAA',
              pci_dss: 'PCI DSS', cmmc: 'CMMC', iso_27001: 'ISO 27001',
            };
            const byFramework = new Map<string, Array<{ control_id: string; control_name: string }>>();
            for (const ctrl of meta?.controls ?? []) {
              const list = byFramework.get(ctrl.framework) ?? [];
              list.push(ctrl);
              byFramework.set(ctrl.framework, list);
            }
            return (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
                <h2 className="text-base font-semibold text-emerald-900 mb-3">Compliance Impact</h2>
                <p className="text-sm text-gray-700 mb-3">{compEntry.description}</p>
                <div className="space-y-2">
                  {[...byFramework.entries()].map(([fw, ctrls]) => (
                    <div key={fw}>
                      <span className="text-xs font-semibold text-emerald-800">{FRAMEWORK_LABELS[fw] ?? fw}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ctrls.map(c => (
                          <span key={c.control_id} className="inline-flex bg-white border border-emerald-200 rounded px-2 py-0.5 text-xs" title={c.control_name}>
                            <span className="font-mono text-emerald-700">{c.control_id}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ARGUS Investigation */}
          {argusError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{argusError}</div>
          )}
          {argusInvestigation && (
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
              <h2 className="text-base font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" /> ARGUS Investigation
              </h2>
              <div className="mb-3">
                <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1">Verdict</p>
                <p className="text-sm font-medium text-gray-800">{argusInvestigation.verdict}</p>
              </div>
              {argusInvestigation.findings.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1">Findings</p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {argusInvestigation.findings.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}
              {argusInvestigation.indicators.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1">Indicators</p>
                  <div className="flex flex-wrap gap-1.5">
                    {argusInvestigation.indicators.map((ind, i) => (
                      <span key={i} className="font-mono text-xs bg-white border border-purple-200 rounded px-2 py-0.5 text-purple-800">{ind}</span>
                    ))}
                  </div>
                </div>
              )}
              {argusInvestigation.full_analysis && (
                <div>
                  <p className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1">Full Analysis</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{argusInvestigation.full_analysis}</p>
                </div>
              )}
            </div>
          )}

          {/* Description / Impact */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Description</h2>
            <p className="text-sm text-gray-600 whitespace-pre-wrap mb-4">{String(incident.description ?? '') || 'No description.'}</p>
            {!!incident.impact_assessment && (
              <>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Impact Assessment</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{String(incident.impact_assessment)}</p>
              </>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Source PSA Ticket — shown when escalated from PSA */}
          {sourcePsaTicketId && (
            <SourceTicketCard
              ticketId={sourcePsaTicketId}
              ticketNumber={sourcePsaTicketNumber}
              ticketUrl={sourcePsaTicketUrl}
            />
          )}

          {/* Customer Info */}
          {enrichedContext?.customer && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" /> Customer
              </h2>
              <div className="space-y-1.5 text-sm">
                {enrichedContext.customer.company_name && <p className="font-medium text-gray-800">{enrichedContext.customer.company_name}</p>}
                {enrichedContext.customer.primary_contact && <p className="text-gray-600">Contact: {enrichedContext.customer.primary_contact}</p>}
                {enrichedContext.customer.sla_tier && (
                  <p className="text-gray-600">SLA: <span className="font-medium capitalize">{enrichedContext.customer.sla_tier}</span></p>
                )}
              </div>
            </div>
          )}

          {/* RMM Status */}
          {enrichedContext?.rmm_status && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-green-500" /> Device Status
              </h2>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-green-600">{enrichedContext.rmm_status.devices_online}</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-red-600">{enrichedContext.rmm_status.devices_offline}</p>
                  <p className="text-xs text-gray-500">Offline</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-amber-600">{enrichedContext.rmm_status.recent_alerts}</p>
                  <p className="text-xs text-gray-500">Alerts</p>
                </div>
              </div>
            </div>
          )}

          {/* Compliance Deadlines */}
          {enrichedContext?.compliance?.notification_required && (
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-400">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-red-500" /> Compliance
              </h2>
              {enrichedContext.compliance.breach_notification_deadline && (
                <p className="text-sm text-red-700 font-medium mb-2">
                  Notification Deadline: {new Date(enrichedContext.compliance.breach_notification_deadline).toLocaleString()}
                </p>
              )}
              {enrichedContext.compliance.applicable_regulations && enrichedContext.compliance.applicable_regulations.length > 0 && (
                <div className="space-y-1">
                  {enrichedContext.compliance.applicable_regulations.map((reg, i) => (
                    <p key={i} className="text-xs text-gray-600">• {reg}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Time Tracking */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-500" /> Time Tracking
            </h2>
            {totalMinutes > 0 && (
              <p className="text-sm text-gray-600 mb-3">
                Total: <span className="font-semibold">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</span>
                {enrichedContext?.time_entries && (
                  <span className="text-gray-400 ml-1">
                    ({enrichedContext.time_entries.billable_minutes}m billable)
                  </span>
                )}
              </p>
            )}
            {timeEntries.length > 0 && (
              <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                {timeEntries.map(te => (
                  <div key={te.id} className="text-xs border-l-2 border-indigo-200 pl-2">
                    <p className="text-gray-700 font-medium">{Math.floor(te.duration_minutes / 60)}h {te.duration_minutes % 60}m — {te.technician_name}</p>
                    {te.description && <p className="text-gray-500">{te.description}</p>}
                    <p className="text-gray-400">{new Date(te.worked_at).toLocaleDateString()} · {te.billable ? 'Billable' : 'Non-billable'}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <div className="flex gap-2">
                <input type="number" min="1" value={timeMinutes} onChange={e => setTimeMinutes(e.target.value)}
                  placeholder="Minutes" className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <input type="text" value={timeDesc} onChange={e => setTimeDesc(e.target.value)}
                  placeholder="Description (optional)" className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={timeBillable} onChange={e => setTimeBillable(e.target.checked)}
                    className="rounded border-gray-300" />
                  Billable
                </label>
                <button onClick={handleLogTime} disabled={addingTime || !timeMinutes}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                  <Plus className="w-3 h-3" />
                  {addingTime ? 'Logging...' : 'Log Time'}
                </button>
              </div>
            </div>
          </div>

          {/* Incident History */}
          {enrichedContext?.incident_history && enrichedContext.incident_history.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500" /> Previous Incidents ({enrichedContext.incident_history.length})
              </h2>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {enrichedContext.incident_history.map(inc => (
                  <button key={inc.id} onClick={() => navigate(`/secops/incidents/${inc.id}`)}
                    className="block w-full text-left text-sm hover:bg-gray-50 px-2 py-1.5 rounded">
                    <p className="text-gray-800 font-medium truncate">{inc.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge value={inc.severity} colors={SEVERITY_COLORS} />
                      <Badge value={inc.status} colors={STATUS_COLORS} />
                      <span className="text-xs text-gray-400">{new Date(inc.opened_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Linked Alerts */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Linked Alerts ({linkedAlerts.length})</h2>
            {linkedAlerts.length === 0 ? (
              <p className="text-sm text-gray-400">No linked alerts.</p>
            ) : (
              <div className="space-y-2">
                {linkedAlerts.map(a => (
                  <button key={a.id} onClick={() => navigate(`/secops/alerts/${a.id}`)}
                    className="block w-full text-left text-sm hover:bg-gray-50 px-2 py-1.5 rounded">
                    <span className="text-indigo-600 hover:underline">{a.title || a.id}</span>
                    <Badge value={a.severity} colors={SEVERITY_COLORS} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Linked Playbooks */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Playbook Executions ({linkedPlaybooks.length})</h2>
            {linkedPlaybooks.length === 0 ? (
              <p className="text-sm text-gray-400">No playbook executions.</p>
            ) : (
              <div className="space-y-2">
                {linkedPlaybooks.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <button onClick={() => navigate(`/secops/playbooks/${p.id}`)} className="text-indigo-600 hover:underline">{p.name}</button>
                    <Badge value={p.status} colors={STATUS_COLORS} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Response Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Response Actions ({responseActions.length})</h2>
            {responseActions.length === 0 ? (
              <p className="text-sm text-gray-400">No response actions taken.</p>
            ) : (
              <div className="space-y-2">
                {responseActions.map(ra => (
                  <div key={ra.id} className="text-sm border-l-2 border-gray-200 pl-3">
                    <p className="font-medium text-gray-800 capitalize">{ra.action_type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">Target: {ra.target} · {ra.status}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Playbook from Incident Confirmation Modal */}
      {showPlaybookConfirm && generatedPlaybook && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPlaybookConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Generated Playbook
            </h3>
            <p className="text-sm text-gray-500 mb-4">Review the generated playbook steps before saving.</p>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-900">{generatedPlaybook.name as string}</p>
              <p className="text-sm text-gray-500 mt-1">{generatedPlaybook.description as string}</p>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Steps ({(generatedPlaybook.steps as Array<Record<string, unknown>>)?.length ?? 0})</p>
              {((generatedPlaybook.steps as Array<Record<string, unknown>>) ?? []).map((step, i) => (
                <div key={i} className="flex items-start gap-2 text-sm border-l-2 border-purple-200 pl-3 py-1">
                  <span className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs flex-shrink-0 mt-0.5">{(step.order as number) ?? i + 1}</span>
                  <div>
                    <p className="font-medium text-gray-800">{step.title as string}</p>
                    {step.description ? <p className="text-xs text-gray-500 mt-0.5">{String(step.description)}</p> : null}
                    <span className="inline-block text-[10px] font-mono bg-gray-100 text-gray-600 rounded px-1 mt-0.5">{step.action_type as string}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
              <button onClick={() => { setShowPlaybookConfirm(false); setGeneratedPlaybook(null); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleConfirmPlaybook} disabled={savingPlaybook}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {savingPlaybook ? 'Saving...' : 'Save Playbook'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
